/* ============================================
   PortalLayout — Client Portal shell
   Sidebar navigation + main content area
   Multi-company selector for affiliated contacts
   ============================================ */

import { Outlet, NavLink, Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useApi } from '@/hooks/useApi'
import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { PortalClienteProvider, usePortalCliente } from '@/hooks/usePortalCliente'
import type { Empresa, MiApp, PortalAppsResponse } from '@/lib/types'
import PwaInstallBanner from './PwaInstallBanner'
import AccountSwitcher from '@/components/auth/AccountSwitcher'
import ToastContainer from '@/components/layout/Toast'
import PortalBottomNav from './PortalBottomNav'
import { puedeVerFacturacion } from '@/lib/portal-permisos'

// Acciones siempre a la mano (Nuevo pedido + Soporte). Se montan en la barra
// superior de desktop y en el topbar fijo de móvil, así están en TODA vista.
// "Nuevo pedido" solo lo ven PRINCIPAL/FACTURACIÓN (o titular legado sin contacto):
// comprar a nombre de la empresa es una acción de esos roles.
function PortalActions() {
  const { user, contactoId } = useAuth()
  const { afiliaciones, activeClienteId } = usePortalCliente()
  const activeAfil = afiliaciones.find(a => a.cliente_id === activeClienteId)
  const roles: string[] = (activeAfil?.roles as string[] | undefined) ?? user?.roles ?? []
  const puedeComprar = puedeVerFacturacion(roles, contactoId)
  return (
    <div className="portal-topbar-actions">
      {puedeComprar && <Link to="/portal/nuevo-pedido" className="btn btn-sm btn-primary">Servicios</Link>}
      <a href="https://wa.me/51986252983?text=Hola%2C%20necesito%20ayuda" target="_blank" rel="noreferrer" className="btn btn-sm btn-secondary">Soporte</a>
    </div>
  )
}

type PortalTheme = 'light' | 'dark'

function IconAppsNav() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" />
    </svg>
  )
}

// Toggle sol/luna del tema del portal (se monta en las topbars)
function ThemeToggle({ theme, onToggle }: { theme: PortalTheme; onToggle: () => void }) {
  const isDark = theme === 'dark'
  return (
    <button
      type="button"
      className="portal-theme-toggle"
      onClick={onToggle}
      aria-label={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
      title={isDark ? 'Modo claro' : 'Modo oscuro'}
    >
      {isDark ? (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
        </svg>
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      )}
    </button>
  )
}

function PortalSidebar({ isOpen, onClose, theme }: { isOpen: boolean; onClose: () => void; theme: PortalTheme }) {
  const { user, contactoId, signOut } = useAuth()
  const api = useApi()
  const navigate = useNavigate()
  const location = useLocation()
  const [empresa, setEmpresa] = useState<Empresa | null>(null)
  // Hosting no es universal: el item solo aparece si la EMPRESA ACTIVA tiene
  // hosting. Se recalcula al cambiar de empresa en el selector (por decision de
  // producto: el menu refleja la empresa seleccionada, aunque cambie de forma).
  const [tieneHosting, setTieneHosting] = useState(false)
  const [hostingProyectoIds, setHostingProyectoIds] = useState<number[]>([])
  const [tieneProyectos, setTieneProyectos] = useState(false)
  // "Horas / Plan" solo si la empresa activa tiene un paquete de horas configurado.
  const [tienePlanHoras, setTienePlanHoras] = useState(false)
  // Apps del cliente (sus activaciones). Se listan como módulos en el nav; las
  // del catálogo que NO tiene activadas no aparecen (esas viven en la vitrina).
  const [misApps, setMisApps] = useState<MiApp[]>([])
  const [appsOpen, setAppsOpen] = useState(false)
  const { afiliaciones, activeClienteId } = usePortalCliente()
  // Determine contact role from user metadata
  // Roles de la EMPRESA activa. Con el switch de arriba, el token ya viene scopeado
  // a la empresa; la afiliación local es el fallback inmediato.
  const activeAfil = afiliaciones.find(a => a.cliente_id === activeClienteId)
  const contactRoles: string[] = (activeAfil?.roles as string[] | undefined) ?? user?.roles ?? []
  const isPrincipal = contactRoles.includes('PRINCIPAL')
  const isContabilidad = contactRoles.includes('FACTURACIÓN') && !isPrincipal
  // Facturas y Suscripción son de PRINCIPAL / FACTURACIÓN. Un contacto TÉCNICO
  // (p. ej. el desarrollador del cliente) entra a operar, no a ver la plata.
  const verFacturacion = puedeVerFacturacion(contactRoles, contactoId)

  useEffect(() => {
    // Stopgap: solo columnas públicas de la empresa (branding/facturación).
    // NUNCA traer google_tokens / canales_config / firma_email_html al cliente.
    // La corrección real es RLS / vista en la DB (ver handoff de seguridad).
    supabase.from('empresa')
      .select('id, nombre, razon_social, ruc, telefono, email, website, ubicacion, logo_url, color_primario, color_secundario, banco, cuenta_corriente, cci, swift, direccion_banco')
      .limit(1).then(({ data }) => {
        if (data && data.length > 0) setEmpresa(data[0] as unknown as Empresa)
      })

    // Activate portal on first login: set portal_activo = true
    const contactoId = user?.user_metadata?.contacto_id as number | undefined
    if (contactoId) {
      ;(async () => {
        const { data } = await supabase
          .from('contactos')
          .select('portal_activo')
          .eq('id', contactoId)
          .single() as unknown as { data: { portal_activo: boolean } | null }
        if (data && !data.portal_activo) {
          await (supabase
            .from('contactos')
            .update({ portal_activo: true } as never)
            .eq('id', contactoId) as unknown as Promise<unknown>)
        }
      })()
    }
  }, [user])

  // ¿La empresa ACTIVA tiene hosting? (decide el item de nav; sigue al selector)
  // Guardamos ademas los proyecto_id que el hosting auto-genera al provisionar: son
  // plomeria interna ("Hosting Starter — midominio.com"), no proyectos del cliente.
  useEffect(() => {
    if (!activeClienteId) { setTieneHosting(false); setHostingProyectoIds([]); return }
    api.get<{ proyecto_id?: number | null }[]>(`/api/hosting/mi-hosting?cliente_id=${activeClienteId}`)
      .then(d => {
        const arr = Array.isArray(d) ? d : []
        setTieneHosting(arr.length > 0)
        setHostingProyectoIds(arr.map(h => h.proyecto_id).filter((v): v is number => v != null))
      })
      .catch(() => { setTieneHosting(false); setHostingProyectoIds([]) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClienteId])

  // "Proyectos" solo si quedan proyectos REALES tras excluir los de hosting. Asi un
  // cliente que solo compro hosting ve Resumen/Facturas/Mi Hosting, sin seccion vacia.
  useEffect(() => {
    if (!activeClienteId) { setTieneProyectos(false); return }
    supabase.from('proyectos').select('id').eq('cliente_id', activeClienteId).then(({ data }) => {
      const ids = ((data ?? []) as unknown as { id: number }[]).map(p => p.id)
      setTieneProyectos(ids.some(id => !hostingProyectoIds.includes(id)))
    })
  }, [activeClienteId, hostingProyectoIds])

  // ¿La empresa activa tiene paquete de horas? (decide el item "Horas / Plan")
  useEffect(() => {
    if (!activeClienteId) { setTienePlanHoras(false); return }
    api.get<{ tiene_plan?: boolean }>(`/api/portal/plan-horas?cliente_id=${activeClienteId}`)
      .then(d => setTienePlanHoras(!!d?.tiene_plan))
      .catch(() => setTienePlanHoras(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClienteId])

  // Apps del cliente. Solo sus activaciones (se excluyen las revocadas): son las
  // que se listan como módulos. Sigue al selector de empresa.
  useEffect(() => {
    if (!activeClienteId) { setMisApps([]); return }
    api.get<PortalAppsResponse>(`/api/portal/apps?cliente_id=${activeClienteId}`)
      .then(d => setMisApps((d?.mis_apps ?? []).filter(a => a.estado !== 'revocada')))
      .catch(() => setMisApps([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClienteId])

  // El grupo queda abierto si ya estás navegando dentro de Apps.
  useEffect(() => {
    if (location.pathname.startsWith('/portal/apps')) setAppsOpen(true)
  }, [location.pathname])

  // Close sidebar on route change (mobile)
  useEffect(() => {
    onClose()
  }, [location.pathname])

  async function handleLogout() {
    await signOut()
    navigate('/login', { replace: true })
  }

  const contactName = user?.user_metadata?.nombre || user?.email || 'Cliente'

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && <div className="portal-sidebar-overlay" onClick={onClose} />}

      <aside className={`portal-sidebar ${isOpen ? 'portal-sidebar--open' : ''}`}>
        <div className="portal-sidebar-brand">
          <img src={theme === 'dark' ? '/assets/brand/logo-full-white.svg' : '/assets/brand/logo-full-color.svg'} alt={empresa?.nombre || '1Conecta'} className="portal-sidebar-logo" />
          <span className="portal-sidebar-label">Portal de Clientes</span>
        </div>

        {/* Selector de cuenta (identidad con varias empresas / partner) */}
        <div className="portal-sidebar-switcher">
          <AccountSwitcher variant={theme === 'dark' ? 'dark' : 'light'} />
        </div>

        <nav className="portal-sidebar-nav">
          {!isContabilidad && (
            <NavLink to="/portal" end className={({ isActive }) => `portal-sidebar-link ${isActive ? 'active' : ''}`}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
              </svg>
              Resumen
            </NavLink>
          )}
          {verFacturacion && (
            <NavLink to="/portal/facturas" className={({ isActive }) => `portal-sidebar-link ${isActive ? 'active' : ''}`}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
              </svg>
              Facturas
            </NavLink>
          )}
          {verFacturacion && (
            <NavLink to="/portal/suscripcion" className={({ isActive }) => `portal-sidebar-link ${isActive ? 'active' : ''}`}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" />
              </svg>
              Suscripción
            </NavLink>
          )}
          {!isContabilidad && tieneProyectos && (
            <NavLink to="/portal/proyectos" className={({ isActive }) => `portal-sidebar-link ${isActive ? 'active' : ''}`}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
              </svg>
              Proyectos
            </NavLink>
          )}
          {!isContabilidad && tienePlanHoras && (
            <NavLink to="/portal/horas" className={({ isActive }) => `portal-sidebar-link ${isActive ? 'active' : ''}`}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
              </svg>
              Horas / Plan
            </NavLink>
          )}
          {!isContabilidad && tieneHosting && (
            <NavLink to="/portal/hosting" className={({ isActive }) => `portal-sidebar-link ${isActive ? 'active' : ''}`}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="8" rx="2" ry="2" /><rect x="2" y="14" width="20" height="8" rx="2" ry="2" /><line x1="6" y1="6" x2="6.01" y2="6" /><line x1="6" y1="18" x2="6.01" y2="18" />
              </svg>
              Hosting
            </NavLink>
          )}
          {/* Apps: sin activaciones es un link directo a la vitrina; con
              activaciones se vuelve un grupo desplegable donde cada app del
              cliente es un módulo. Las del catálogo que no tiene NO aparecen. */}
          {!isContabilidad && (
            misApps.length === 0 ? (
              <NavLink to="/portal/apps" className={({ isActive }) => `portal-sidebar-link ${isActive ? 'active' : ''}`}>
                <IconAppsNav />
                Apps
              </NavLink>
            ) : (
              <div className="portal-nav-group">
                <button
                  type="button"
                  className={`portal-sidebar-link portal-nav-group-toggle ${location.pathname.startsWith('/portal/apps') ? 'active' : ''}`}
                  onClick={() => setAppsOpen(o => !o)}
                  aria-expanded={appsOpen}
                >
                  <IconAppsNav />
                  Apps
                  <span className="portal-nav-count">{misApps.length}</span>
                  <svg className={`portal-nav-chevron ${appsOpen ? 'portal-nav-chevron--open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                {appsOpen && (
                  <div className="portal-nav-sub">
                    {misApps.map(a => (
                      <NavLink
                        key={a.id}
                        to={`/portal/apps/${a.app_slug}`}
                        className={({ isActive }) => `portal-sidebar-sublink ${isActive ? 'active' : ''}`}
                        title={a.nombre ?? a.app_slug}
                      >
                        {a.icono_url
                          ? <img className="portal-sub-logo" src={a.icono_url} alt="" />
                          : <span className="portal-sub-logo portal-sub-logo--txt">{(a.nombre ?? a.app_slug).charAt(0).toUpperCase()}</span>}
                        <span className="portal-sub-name">{a.nombre ?? a.app_slug}</span>
                        {a.estado === 'pendiente' && <span className="portal-sub-dot" title="Pendiente de canje" />}
                      </NavLink>
                    ))}
                    <NavLink to="/portal/apps" end className={({ isActive }) => `portal-sidebar-sublink portal-sidebar-sublink--all ${isActive ? 'active' : ''}`}>
                      Ver todas las apps
                    </NavLink>
                  </div>
                )}
              </div>
            )
          )}
        </nav>


        <div className="portal-sidebar-footer">
          <NavLink to="/portal/perfil" className={({ isActive }) => `portal-sidebar-user ${isActive ? 'active' : ''}`} title={contactName}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
            </svg>
            <span className="portal-sidebar-username">Mi perfil</span>
          </NavLink>
          <button className="portal-sidebar-logout" onClick={handleLogout}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Cerrar sesión
          </button>
        </div>
      </aside>
    </>
  )
}

export default function PortalLayout() {
  const { isCliente } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  // Tema del portal: SIEMPRE light la primera vez. Si el cliente elige dark,
  // se persiste en localStorage y se respeta en visitas siguientes.
  const [theme, setTheme] = useState<PortalTheme>(() => {
    try {
      return localStorage.getItem('portal_theme') === 'dark' ? 'dark' : 'light'
    } catch {
      return 'light'
    }
  })
  useEffect(() => {
    try { localStorage.setItem('portal_theme', theme) } catch { /* ignore */ }
  }, [theme])
  const toggleTheme = () => setTheme(t => (t === 'light' ? 'dark' : 'light'))

  if (!isCliente) return null

  return (
    <PortalClienteProvider>
      <div className="portal-shell" data-theme={theme}>
        {/* Mobile top bar */}

        <PortalSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} theme={theme} />
        <div className="portal-content-area">
          {/* La ruta (breadcrumbs) se dibuja acá: las páginas la mandan a este
              slot en vez de pintarla sobre su contenido. */}
          <div className="portal-topbar">
            <div id="portal-crumbs-slot" className="portal-topbar-crumbs" />
            <div className="portal-topbar-right">
              <ThemeToggle theme={theme} onToggle={toggleTheme} />
              <PortalActions />
            </div>
          </div>
          <PwaInstallBanner />
          <main className="portal-main">
            <Outlet />
          </main>
          <footer className="portal-footer">
            <p>© 2026 1Conecta — Portal de Clientes</p>
          </footer>
        </div>
        <PortalBottomNav theme={theme} onToggleTheme={toggleTheme} />
        <ToastContainer />
      </div>
    </PortalClienteProvider>
  )
}

